import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  IconButton,
  Box,
  List,
  ListItem,
  ListItemText,
  Button,
  Tooltip,
  useTheme,
  useMediaQuery,
  Divider
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Timer as TimerIcon,
  Add as AddIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { WorkoutSet } from '../../types';
import { useLanguage } from '../../i18n/LanguageContext';
import { useTimer } from '../../contexts/TimerContext';
import { RecentWorkoutsDialog } from '../workouts/RecentWorkoutsDialog';
import { useState } from 'react';
import { WorkoutService } from '../../services/WorkoutService';

interface WorkoutSetItemProps {
  workout: WorkoutSet;
  index: number;
  expanded: boolean;
  onToggleExpand: (index: number) => void;
  onEdit: (workout: WorkoutSet) => void;
  onDelete: (workoutId: number | undefined) => void;
  onDeleteDetail: (detailId: number | undefined) => void;
  onQuickAddSet?: (workoutId: number | undefined) => void;
  onQuickEditSet?: (workoutId: number, setDetail: any) => void;
}

export const WorkoutSetItem: React.FC<WorkoutSetItemProps> = ({
  workout,
  index,
  expanded,
  onToggleExpand,
  onEdit,
  onDelete,
  onDeleteDetail,
  onQuickAddSet,
  onQuickEditSet
}) => {
  const { t } = useLanguage();
  const { startTimer } = useTimer();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [recentWorkoutsOpen, setRecentWorkoutsOpen] = useState(false);
  const [recentWorkouts, setRecentWorkouts] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(false);

  const handleOpenRecentWorkouts = async () => {
    if (!workout.exercise_id) {
      return;
    }
    setRecentWorkoutsOpen(true);
    setLoadingRecent(true);
    try {
      const data = await WorkoutService.getExerciseDetails(workout.exercise_id);
      setRecentWorkouts(data.recentWorkouts);
    } catch (error) {
      console.error('Failed to fetch recent workouts', error);
    } finally {
      setLoadingRecent(false);
    }
  };

  const handleAccordionChange = (event: React.SyntheticEvent, isExpanded: boolean) => {
    onToggleExpand(index);
  };

  const handleStartTimer = (restTimeSeconds?: number) => {
    const timerDuration = restTimeSeconds || 90; // Default to 90 seconds
    startTimer(timerDuration);
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={handleAccordionChange}
      sx={{ mb: 1 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mr: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
              {workout.workout_type === 'amrap' ? (
                `AMRAP - ${Math.floor((workout.amrap_time_seconds || 0) / 60)}min`
              ) : (
                workout.exercise
              )}
            </Typography>
            {workout.comment && (
              <Typography variant="body2" color="text.secondary">
                {t('workout.comment')}: {workout.comment}
              </Typography>
            )}
            {workout.workout_type === 'amrap' && workout.amrap_details && (
              <Typography variant="body2" color="text.secondary">
                {workout.amrap_details.length} {workout.amrap_details.length === 1 ? t('workout.exerciseSingular') : t('workout.exercisePlural')}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title={t('workout.edit')}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(workout);
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('workout.delete')}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(workout.id);
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
            {workout.workout_type !== 'amrap' && (
              <Tooltip title={t('recentWorkouts.title', { exerciseName: workout.exercise })}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenRecentWorkouts();
                  }}
                >
                  <HistoryIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ width: '100%' }}>
          {workout.workout_type === 'amrap' ? (
            // AMRAP workout display
            workout.amrap_details && workout.amrap_details.length > 0 ? (
              <List disablePadding>
                {workout.amrap_details.map((amrapDetail, detailIdx) => (
                  <ListItem key={detailIdx} sx={{ px: 0, py: 1 }}>
                    <ListItemText
                      primary={
                        <Typography variant="body2" color="text.primary">
                          {`${amrapDetail.exercise_name}: ${amrapDetail.reps} ${t('workout.repsUnit')}`}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('workout.noExercisesRecorded')}
              </Typography>
            )
          ) : (
            // Traditional workout display
            workout.details && workout.details.length > 0 ? (
              <List disablePadding>
                {workout.details.map((detail, detailIdx) => (
                  <Box key={detailIdx}>
                    <ListItem sx={{ px: 0, py: 1 }}>
                      <Box sx={{ display: 'flex', width: '100%', alignItems: 'flex-start' }}>
                        {/* Left column - Set details */}
                        <Box sx={{ flex: 1, pr: 2 }}>
                          <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
                            {`${t('workout.series')}: `}<Box component="span" sx={{ fontWeight: 'bold' }}>{detail.series}</Box>
                          </Typography>
                          <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
                            {`${t('workout.reps')}: `}<Box component="span" sx={{ fontWeight: 'bold' }}>{detail.reps}</Box>
                          </Typography>
                          <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
                            {`${t('workout.weight')}: `}<Box component="span" sx={{ fontWeight: 'bold' }}>{detail.weight} kg</Box>
                          </Typography>
                          {detail.rest_time_seconds != null && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              {`${t('workout.restTime')}: ${detail.rest_time_seconds}s`}
                            </Typography>
                          )}
                          {detail.rir && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              {`${t('workout.rir')}: ${detail.rir}`}
                            </Typography>
                          )}
                        </Box>
                        
                        {/* Right column - Action buttons */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
                          <Tooltip title={t('timer.startTimer')}>
                            <Button 
                              variant="outlined" 
                              color="primary" 
                              size="small"
                              startIcon={<TimerIcon />}
                              onClick={() => handleStartTimer(detail.rest_time_seconds)}
                            >
                              {detail.rest_time_seconds ? `${detail.rest_time_seconds}s` : '90s'}
                            </Button>
                          </Tooltip>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {onQuickEditSet && (
                              <Tooltip title={t('workout.editSet')}>
                                {isMobile ? (
                                  <IconButton
                                    color="secondary"
                                    size="small"
                                    onClick={() => onQuickEditSet(workout.id!, detail)}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                ) : (
                                  <Button 
                                    variant="outlined" 
                                    color="secondary" 
                                    size="small"
                                    startIcon={<EditIcon />}
                                    onClick={() => onQuickEditSet(workout.id!, detail)}
                                  >
                                    {t('workout.edit')}
                                  </Button>
                                )}
                              </Tooltip>
                            )}
                            {workout.details.length > 1 && (
                              <Tooltip title={t('workout.delete')}>
                                {isMobile ? (
                                  <IconButton
                                    color="error"
                                    size="small"
                                    onClick={() => onDeleteDetail(detail.id)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                ) : (
                                  <Button 
                                    variant="outlined" 
                                    color="error" 
                                    size="small"
                                    startIcon={<DeleteIcon />}
                                    onClick={() => onDeleteDetail(detail.id)}
                                  >
                                    {t('workout.delete')}
                                  </Button>
                                )}
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </ListItem>
                    {detailIdx < workout.details.length - 1 && (
                      <Divider sx={{ my: 1, mx: 2 }} />
                    )}
                  </Box>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('workout.noDetails')}
              </Typography>
            )
          )}
          {/* Quick Add Set Button - only for traditional workouts when expanded */}
          {workout.workout_type !== 'amrap' && onQuickAddSet && expanded && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => onQuickAddSet(workout.id)}
                size="small"
              >
                {t('workout.addSet')}
              </Button>
            </Box>
          )}
        </Box>
      </AccordionDetails>
      <RecentWorkoutsDialog
        open={recentWorkoutsOpen}
        onClose={() => setRecentWorkoutsOpen(false)}
        exerciseName={workout.exercise || ''}
        recentWorkouts={recentWorkouts}
        loading={loadingRecent}
      />
    </Accordion>
  );
};